import React, { useState, useEffect, useRef } from 'react';
import './PaymentTerminal.css';

/**
 * Payment Terminal Component
 * Shows a hacker-style terminal sequence before payment initialization
 */
const PaymentTerminal = ({ isOpen, onComplete, paymentData }) => {
  const [logs, setLogs] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const logsEndRef = useRef(null);

  const terminalSteps = [
    { 
      text: '> Initializing secure payment gateway...', 
      delay: 300,
      color: '#00ff41'
    },
    { 
      text: '> Establishing encrypted connection to Hubtel API...', 
      delay: 500,
      color: '#00ff41'
    },
    { 
      text: '> Connection established [SSL/TLS v1.3]', 
      delay: 400,
      color: '#00ffff'
    },
    { 
      text: `> Verifying merchant credentials...`, 
      delay: 600,
      color: '#00ff41'
    },
    { 
      text: '> Merchant verified ✓', 
      delay: 300,
      color: '#00ff00'
    },
    { 
      text: `> Validating transaction amount: GHS ${paymentData?.amount || '0.00'}`, 
      delay: 500,
      color: '#00ff41'
    },
    { 
      text: '> Amount validation passed ✓', 
      delay: 300,
      color: '#00ff00'
    },
    { 
      text: `> Detecting mobile network: ${paymentData?.carrier?.toUpperCase() || 'UNKNOWN'}`, 
      delay: 600,
      color: '#ffff00'
    },
    { 
      text: `> Formatting phone number: +233 ${paymentData?.phoneNumber || ''}`, 
      delay: 400,
      color: '#00ff41'
    },
    { 
      text: '> Preparing transaction payload...', 
      delay: 500,
      color: '#00ff41'
    },
    { 
      text: '> Encrypting sensitive data [AES-256]...', 
      delay: 700,
      color: '#ff6600'
    },
    { 
      text: '> Encryption complete ✓', 
      delay: 300,
      color: '#00ff00'
    },
    { 
      text: '> Generating unique transaction reference...', 
      delay: 500,
      color: '#00ff41'
    },
    { 
      text: `> Reference generated: ${paymentData?.reference || 'REF-XXXXXX'}`, 
      delay: 400,
      color: '#00ffff'
    },
    { 
      text: '> All systems ready...', 
      delay: 500,
      color: '#00ff41'
    },
    { 
      text: '> Initializing payment request...', 
      delay: 600,
      color: '#ff00ff'
    },
    { 
      text: '========================================', 
      delay: 200,
      color: '#00ff41'
    },
    { 
      text: '> TERMINAL SEQUENCE COMPLETE', 
      delay: 300,
      color: '#00ff00',
      blink: true
    },
    { 
      text: '> Redirecting to payment interface...', 
      delay: 500,
      color: '#00ffff'
    }
  ];

  // Auto-scroll to bottom
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Run terminal sequence
  useEffect(() => {
    if (!isOpen) {
      // Reset when closed
      setLogs([]);
      setCurrentStep(0);
      setIsComplete(false);
      return;
    }

    let timeoutId;
    let isCancelled = false;

    const runSequence = async () => {
      for (let i = 0; i < terminalSteps.length; i++) {
        if (isCancelled) break;
        
        const step = terminalSteps[i];
        
        // Add log with typing effect
        await new Promise(resolve => {
          timeoutId = setTimeout(() => {
            if (!isCancelled) {
              setLogs(prev => [...prev, step]);
              setCurrentStep(i);
            }
            resolve();
          }, step.delay);
        });
      }

      if (!isCancelled) {
        setIsComplete(true);
        // Notify parent that terminal is complete
        setTimeout(() => {
          if (!isCancelled && onComplete) {
            onComplete();
          }
        }, 800);
      }
    };

    // Small initial delay before starting
    timeoutId = setTimeout(() => {
      runSequence();
    }, 500);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isOpen, paymentData]);

  if (!isOpen) return null;

  return (
    <div className="terminal-overlay">
      <div className="terminal-container">
        {/* Terminal Header */}
        <div className="terminal-header">
          <div className="terminal-title">
            <i className="fas fa-terminal"></i>
            <span>KAMELOT TERMINAL</span>
          </div>
          <div className="terminal-controls">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot yellow"></span>
            <span className="terminal-dot green"></span>
          </div>
        </div>

        {/* Terminal Body */}
        <div className="terminal-body">
          <div className="terminal-info-bar">
            <span className="terminal-status">
              <i className="fas fa-shield-alt"></i>
              SECURE CONNECTION
            </span>
            <span className="terminal-protocol">
              <i className="fas fa-lock"></i>
              SSL/TLS v1.3
            </span>
            <span className="terminal-progress">
              {Math.round((currentStep / (terminalSteps.length - 1)) * 100)}%
            </span>
          </div>

          <div className="terminal-logs">
            {logs.map((log, index) => (
              <div 
                key={index} 
                className={`terminal-line ${log.blink ? 'blink' : ''}`}
                style={{ color: log.color }}
              >
                {log.text}
              </div>
            ))}
            {!isComplete && (
              <div className="terminal-line cursor">
                <span className="terminal-prompt">root@quantumpay:~$</span>
                <span className="terminal-cursor">_</span>
              </div>
            )}
            <div ref={logsEndRef} />
          </div>

          {/* Progress Bar */}
          <div className="terminal-progress-bar">
            <div 
              className="terminal-progress-fill"
              style={{ 
                width: `${(currentStep / (terminalSteps.length - 1)) * 100}%`,
                transition: 'width 0.3s ease-out'
              }}
            />
          </div>
        </div>

        {/* Terminal Footer */}
        <div className="terminal-footer">
          <div className="terminal-footer-left">
            <i className="fas fa-server"></i>
            <span>Hubtel Payment Gateway</span>
          </div>
          <div className="terminal-footer-right">
            {isComplete ? (
              <span className="terminal-complete">
                <i className="fas fa-check-circle"></i>
                COMPLETE
              </span>
            ) : (
              <span className="terminal-processing">
                <i className="fas fa-circle-notch fa-spin"></i>
                PROCESSING...
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTerminal;
